import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { zhTW } from 'date-fns/locale';

import firebase from '@lib/firebase';
import { Config, TemplateVariables } from '@lib/emails/sendSuccessPurchased';
import { CreateCheckoutWithTappay } from '@lib/kaik';
import { EventType } from '@lib/types';
import { PaymentEnum, usePayment } from '@providers/PaymentProvider';
import { asyncPost, post } from '@lib/fetch';
import { redeemGenerator } from '@lib/utils';
import { useAccountContext } from '@lib/hooks';

import SignIn from '../../form/SignIn';

import TicketsSection from './TicketsSection';
import UserInfoSection from './UserInfoSection';

type Props = {
  tickets: any;
  selectedTicket: any;
  checkoutAmount: number;
  event: EventType;
  onSuccess: any;
  onError: any;
  onTicketIdChange: any;
  onQuantityChange: any;
};

const TicketBlock = ({
  tickets,
  selectedTicket,
  checkoutAmount,
  event,
  onSuccess,
  onError,
  onTicketIdChange,
  onQuantityChange,
}: Props) => {
  const toast = useToast();
  const { uid, user, handleRefetchUid } = useAccountContext();

  // UI related flag
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailDuplicate, setIsEmailDuplicate] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);

  const DEFAULT_TICKET_ID = event?.tickets?.[0].id;
  const DEFAULT_QUANTITY = 1;

  const methods = useForm<{
    quantity: number;
    ticketId: string;
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    password: string;
  }>({
    mode: 'onBlur',
    defaultValues: {
      ticketId: DEFAULT_TICKET_ID,
      quantity: DEFAULT_QUANTITY,
    },
  });

  const { handleSubmit, formState, watch } = methods;

  const formValues = watch();

  const { isValid, isSubmitting } = formState;

  const userLoggedInEmail = uid && user?.email;

  const { t } = useTranslation(['common']);

  const handleLoginSuccess = useCallback(
    (uid: string) => {
      handleRefetchUid(uid);
      setShowLoginForm(false);
      setIsEmailDuplicate(false);
    },
    [handleRefetchUid]
  );

  const handleSignUserUp = useCallback(async () => {
    const { firstName, lastName, email, password } = formValues;

    // Create account in firebase for user if not logged in
    if (!userLoggedInEmail && password) {
      const userCredential = await firebase
        .auth()
        .createUserWithEmailAndPassword(email, password);

      if (!userCredential) {
        toast({ title: 'Create user failed', status: 'error' });
        throw 'Create user failed';
      }

      const { user } = userCredential;

      if (!user) {
        toast({ title: 'Create user failed', status: 'error' });
        throw 'Create user failed';
      }

      const uid = user?.uid;

      post({
        url: '/api/webhooks/signup',
        body: {
          email,
          from: 'ticket',
          event: event.name,
          amount: checkoutAmount,
        },
        onSuccess: () => undefined,
        onError: () => undefined,
      });

      // Create user in realtime database
      await firebase
        .database()
        .ref(`/${process.env.NODE_ENV}/user/${uid}`)
        .set({ email, stream: [], events: [], firstName, lastName });

      handleRefetchUid(uid);
    }
  }, [
    checkoutAmount,
    event.name,
    formValues,
    handleRefetchUid,
    toast,
    userLoggedInEmail,
  ]);

  const handleProcessRedeemCodes = useCallback(
    async ({ transactionId }: { transactionId: string }) => {
      const { firstName, lastName, email, quantity } = formValues;

      // Used loggedIn email if provided;
      const buyerEmail = userLoggedInEmail ? userLoggedInEmail : email;

      // Create transaction and redeem codes in firebase
      const redeemCodes = [];
      for (let i = 0; i < quantity; i++) {
        const redeemCode = redeemGenerator(8);
        redeemCodes.push(redeemCode);
        // Save to firebase and keep relation between transaction and event for validation in future
        await firebase
          .database()
          .ref(`/${process.env.NODE_ENV}/redeemCodes/${event.id}/${redeemCode}`)
          .set({
            id: redeemCode,
            ticketId: selectedTicket.id,
            eventId: event.id,
            email: i === 0 ? buyerEmail : `_${buyerEmail}`,
            transactionId,
          });
      }

      await firebase
        .database()
        .ref(`/${process.env.NODE_ENV}/transactions/${transactionId}`)
        .set({
          eventId: event.id,
          ticketId: selectedTicket.id,
          quantity,
          redeemCodes,
        });

      const redeemCode = redeemCodes[0];

      const templateVariables: TemplateVariables = {
        username: `${lastName} ${firstName}`,
        redeemCode,
        eventName: event.name,
        eventStartedAt: `${format(
          new Date(fromUnixTime(event.startedAt)),
          'PPPP p',
          {
            locale: zhTW,
          }
        )}`,
        eventLink: `https://nutshell.live/events/${event.id}`,
      };

      const config: Config = {
        to: buyerEmail,
      };

      const emailResponse = await fetch('/api/email/sendSuccessPurchased', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: templateVariables,
          config,
        }),
      });

      if (emailResponse.status !== 200) {
        // FIXME: Catch ajax unexpected status
        throw 'Send email failed';
      }

      onSuccess(redeemCode);
    },
    [
      event.id,
      event.name,
      event.startedAt,
      formValues,
      onSuccess,
      selectedTicket.id,
      userLoggedInEmail,
    ]
  );

  const handleProcessTappay = useCallback(
    async (prime: string) => {
      if (!formValues || !selectedTicket) {
        return;
      }

      setIsLoading(true);

      try {
        const { firstName, lastName, email, mobile, quantity } = formValues;

        // Used loggedIn email if provided;
        const buyerEmail = userLoggedInEmail ? userLoggedInEmail : email;

        const result = (await asyncPost({
          url: `/api/mutation/createCheckoutWithTappay`,
          body: {
            prime,
            amount: checkoutAmount,
            lineitems: [
              {
                lineitemType: 'Event',
                lineitemId: event.id,
                name: `${event.name} - ${selectedTicket.name}`,
                quantity:
                  typeof quantity === 'string'
                    ? parseInt(quantity, 10)
                    : quantity,
              },
            ],
            cardholder: {
              name: `${lastName} ${firstName}`,
              email: buyerEmail,
              phoneNumber: mobile ?? '', // not collect phoneNumber on free ticket
            },
            env:
              process.env.NODE_ENV === 'production' ? 'production' : 'sandbox',
          },
        })) as { createCheckoutWithTappay: CreateCheckoutWithTappay };

        const { id } = result.createCheckoutWithTappay;
        await handleProcessRedeemCodes({ transactionId: id });
      } catch (e) {
        setIsLoading(false);

        if (e.response && e.response.errors) {
          onError(e.response.errors[0].message);
        } else if (typeof e === 'string') {
          onError(e);
        } else {
          onError(e.toString());
        }

        // Let sentry catch it
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [
      formValues,
      selectedTicket,
      userLoggedInEmail,
      checkoutAmount,
      event.id,
      event.name,
      handleProcessRedeemCodes,
      onError,
    ]
  );

  const { paymentElement, onSubmit, canGetPrime, type } = usePayment();

  const isFreeTicket = selectedTicket.price == 0;

  const handlePurchase = useCallback(async () => {
    try {
      // Use another loading flag instead of isSubmit for react-hook-form
      // Due to the promise / non-promise function mixed cannot keep isSubmitting in the event loop
      setIsLoading(true);

      if (isFreeTicket) {
        await handleSignUserUp();
        await handleProcessTappay('0000');
        return;
      }

      // For tappay, it will respond prime for us to process in Kaik Api
      if (type === PaymentEnum.Tappay) {
        const prime = await onSubmit();

        await handleSignUserUp();
        await handleProcessTappay(prime);
      } else if (type === PaymentEnum.Stripe) {
        // For stripe, it will respond a id which we should take it as transaction id
        const { firstName, lastName, email } = formValues;

        // Used loggedIn email if provided;
        const buyerEmail = userLoggedInEmail ? userLoggedInEmail : email;

        const transactionId = await onSubmit({
          email: buyerEmail,
          name: `${firstName} ${lastName}`,
          price: checkoutAmount,
          eventId: event.id,
          commission: 0,
        });

        await handleSignUserUp();
        await handleProcessRedeemCodes({ transactionId });
      }
    } catch (err) {
      toast({ title: err, status: 'error' });
    }
  }, [
    isFreeTicket,
    type,
    handleSignUserUp,
    handleProcessTappay,
    onSubmit,
    formValues,
    userLoggedInEmail,
    checkoutAmount,
    event.id,
    handleProcessRedeemCodes,
    toast,
  ]);

  return (
    <Flex
      flex={7}
      p={4}
      pr="10%"
      flexDirection="column"
      minWidth={{ base: '100%', md: '600px' }}
    >
      {/* Log In form should be outside the purchase form, since form cannot wrap another form */}
      {showLoginForm && (
        <Box width="full" mt={4} mb={4}>
          <SignIn onSuccess={handleLoginSuccess} />
        </Box>
      )}
      <FormProvider {...methods}>
        <form
          onSubmit={
            showLoginForm
              ? (e) => e.preventDefault() // Don't submit when user client Log In form's login button
              : handleSubmit(handlePurchase)
          }
        >
          <UserInfoSection
            isFreeTicket={isFreeTicket}
            setShowLoginForm={setShowLoginForm}
            showLoginForm={showLoginForm}
            isEmailDuplicate={isEmailDuplicate}
            setIsEmailDuplicate={setIsEmailDuplicate}
          />

          <TicketsSection
            tickets={tickets}
            selectedTicket={selectedTicket}
            onTicketIdChange={onTicketIdChange}
            onQuantityChange={onQuantityChange}
          />

          <Box style={{ display: !isFreeTicket ? 'block' : 'none' }}>
            <Heading
              fontSize="l"
              fontWeight="light"
              pb={4}
              width="full"
              borderBottom="1px"
              borderColor="gray.300"
              mt={4}
            >
              {t('Payment Information')}
            </Heading>
            <Box pt={4} pb={4}>
              {paymentElement}
            </Box>
          </Box>
          <Divider my={8} />
          <Button
            type="submit"
            colorScheme="brand"
            width="full"
            isLoading={isLoading}
            disabled={
              (!isFreeTicket && !canGetPrime) ||
              !isValid ||
              isSubmitting ||
              isEmailDuplicate
            }
          >
            {t('Confirm and Purchase Tickets')}
          </Button>

          {!isFreeTicket && (
            <Text
              color="orange.500"
              fontSize="xs"
              fontWeight="bold"
              textAlign="left"
              mt={2}
            >
              {type === PaymentEnum.Tappay ? (
                <>
                  {t(
                    'By clicking the button, we will process the payment immediately.'
                  )}
                  本網站採用喬睿科技 TapPay SSL 交易傳輸系統，該系統通過 PCI-DSS
                  國際信用卡組織 VISA、MasterCard 等產業資料安全 Level 1
                  等級，完善保護您的付款傳輸資料。
                </>
              ) : (
                <>
                  {t('Payment service provided by stripe')}
                  <br />
                  {t(
                    'By clicking the button, we will process the payment immediately.'
                  )}
                </>
              )}
            </Text>
          )}
        </form>
      </FormProvider>
    </Flex>
  );
};

export default TicketBlock;
